/**
 * Slot-based mount API for the KERN Feedback Widget.
 *
 * Mirrors the integration style of the KERN Chatbot widget
 * (`KernChatbot.mountChatWidget("#kern-chatbot", { … })`): the host page
 * declares a placeholder element for the widget icon, loads the bundle, and
 * mounts the widget into that placeholder.
 *
 * ```html
 * <div id="kern-feedback"></div>
 * <script src="https://feedback.kern-ux.de/widget/feedback-widget.js"></script>
 * <script>
 *   KernFeedback.mountFeedbackWidget('#kern-feedback', {
 *     apiKey: 'YOUR_API_KEY',
 *     apiUrl: 'https://feedback.kern-ux.de/api/v1',
 *     lightDarkMode: 'auto',
 *   });
 * </script>
 * ```
 *
 * ## What actually goes into the slot
 *
 * Only the **compact icon trigger of the mobile layout (≤ 574px)** is rendered
 * into the slot, in the normal document flow. The desktop floating button
 * (≥ 575px), the dialog and the backdrop stay in a container appended to
 * `document.body`.
 *
 * That placement is deliberate and is the one behavioural difference to the
 * chatbot widget, which keeps its `position: fixed` launcher and dialog
 * *inside* the mount element. Fixed descendants are trapped by any ancestor
 * that establishes a containing block (`transform`, `filter`, `perspective`,
 * `will-change`, `contain`, `backdrop-filter`, …) and are suppressed entirely
 * by a `display: none` ancestor. Keeping the overlays in `<body>` means:
 *
 * - the host may hide the slot on desktop (`display: none`) — the floating
 *   button and the dialog are unaffected;
 * - the host may place the slot inside a transformed/animated header — the
 *   dialog is never clipped;
 * - a missing slot degrades gracefully to a fixed corner icon instead of an
 *   invisible widget.
 *
 * ## Lifecycle
 *
 * The widget is a **document-level singleton**: it renders IDs and a
 * page-scroll lock that only make sense once per page, so a second mount call
 * warns and returns the existing mount instead of creating a duplicate. The
 * authoritative registry is a property on the slot element itself (so a second
 * copy of the bundle sees it too); `data-kern-feedback-mounted` is the visible
 * marker for debugging. A mount whose slot has been detached from the document
 * (a SPA re-render that dropped the slot without calling `unmount()`) is
 * reclaimed automatically on the next mount call.
 */

import { FeedbackWidget, WIDGET_CONTAINER_PROPERTY } from './FeedbackWidget.js';
import type { FeedbackWidgetConfig } from './types.js';

/** Marker attribute set on the slot while a widget is mounted into it. */
export const MOUNT_MARKER_ATTRIBUTE = 'data-kern-feedback-mounted';

/** Property carrying the mount handle on the slot element (cross-bundle safe). */
const MOUNT_PROPERTY = '__kernFeedbackMount';

/**
 * Options for {@link mountFeedbackWidget}.
 *
 * `triggerContainer` is omitted because the mount target *is* the trigger
 * container, and `autoInit` because `mountFeedbackWidget` always initialises
 * the widget itself.
 */
export type MountFeedbackWidgetOptions = Omit<
  FeedbackWidgetConfig,
  'triggerContainer' | 'autoInit'
>;

/** Handle returned by {@link mountFeedbackWidget}. */
export interface FeedbackWidgetMount {
  /** The underlying widget instance (full API: `open`, `close`, `destroy`, …). */
  readonly widget: FeedbackWidget;
  /** The slot element the widget was mounted into. */
  readonly target: HTMLElement;
  /** `false` once `unmount()` has run, or once the widget was destroyed directly. */
  readonly isMounted: boolean;
  /** Open the feedback dialog programmatically. */
  open(): void;
  /** Close the feedback dialog programmatically. */
  close(): void;
  /** Remove the widget from the page. Idempotent. */
  unmount(): void;
}

function isFeedbackWidgetMount(value: unknown): value is FeedbackWidgetMount {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as FeedbackWidgetMount).unmount === 'function' &&
    typeof (value as FeedbackWidgetMount).isMounted === 'boolean'
  );
}

function readMount(element: Element): FeedbackWidgetMount | null {
  const value = (element as unknown as Record<string, unknown>)[MOUNT_PROPERTY];
  return isFeedbackWidgetMount(value) ? value : null;
}

function writeMount(element: HTMLElement, mount: FeedbackWidgetMount | null): void {
  // `configurable: true` so the property can be replaced/removed on unmount.
  Object.defineProperty(element, MOUNT_PROPERTY, {
    value: mount ?? undefined,
    configurable: true,
    enumerable: false,
    writable: true,
  });
}

/** The mount owned by this bundle copy, if any (see {@link findActiveMount}). */
let activeMount: FeedbackWidgetMount | null = null;

/**
 * Resolve a CSS selector or element into the slot element.
 * Throws when the target cannot be resolved — an explicit mount target that
 * does not exist is a host-page integration error and must not be papered over.
 */
function resolveMountTarget(target: string | HTMLElement): HTMLElement {
  if (target instanceof HTMLElement) {
    return target;
  }

  if (typeof target !== 'string') {
    throw new TypeError('mountFeedbackWidget: target must be a CSS selector or an HTMLElement.');
  }

  let element: Element | null;
  try {
    element = document.querySelector(target);
  } catch {
    throw new Error(`mountFeedbackWidget: invalid selector: ${target}`);
  }

  if (!(element instanceof HTMLElement)) {
    throw new Error(`mountFeedbackWidget: target not found: ${target}`);
  }

  return element;
}

/**
 * Find the widget currently mounted in this document, reclaiming a mount whose
 * slot was detached without `unmount()` (SPA re-render) and clearing a stale
 * marker left behind by a different bundle copy.
 */
function findActiveMount(): FeedbackWidgetMount | null {
  // Primary registry. Needed in addition to the DOM marker because a slot that
  // a framework removed from the document is no longer reachable via
  // `querySelectorAll`, while its widget (dialog + floating button live in
  // `<body>`) is very much still alive.
  if (activeMount) {
    if (!activeMount.isMounted) {
      // Either already unmounted, or the widget was destroyed directly through
      // `mount.widget.destroy()` / `window.KernFeedback.destroy()`. Run the
      // mount cleanup (marker, element property, global) so the slot is
      // reusable instead of being wedged by a dead handle.
      activeMount.unmount();
      activeMount = null;
    } else if (!activeMount.target.isConnected) {
      console.warn(
        'KERN Feedback: the mount slot was removed from the document without calling unmount() — reclaiming the previous mount.'
      );
      activeMount.unmount();
      activeMount = null;
    } else {
      return activeMount;
    }
  }

  // Secondary: a marker from another copy of the bundle on the same page.
  const markers = document.querySelectorAll(`[${MOUNT_MARKER_ATTRIBUTE}]`);
  for (const marker of Array.from(markers)) {
    const mount = readMount(marker);
    if (mount && mount.isMounted) {
      return mount;
    }
    // Stale attribute (HMR, cloned markup, a torn-down mount): clean it up
    // rather than blocking every future mount.
    marker.removeAttribute(MOUNT_MARKER_ATTRIBUTE);
  }

  return null;
}

/**
 * Publish the mounted widget on `window.KernFeedback` without clobbering the
 * bundle's export namespace (which carries `FeedbackWidget`,
 * `mountFeedbackWidget`, …). Earlier versions replaced the whole global with
 * the instance, which made `KernFeedback.FeedbackWidget` disappear after
 * auto-init.
 */
export function exposeGlobalFeedbackApi(widget: FeedbackWidget | null): void {
  if (typeof window === 'undefined') return;

  const globalScope = window as Window & { KernFeedback?: Record<string, unknown> };
  const namespace: Record<string, unknown> =
    typeof globalScope.KernFeedback === 'object' && globalScope.KernFeedback !== null
      ? globalScope.KernFeedback
      : {};

  const api: Record<string, unknown> = {
    instance: widget ?? undefined,
    init: () => widget?.init(),
    open: () => widget?.open(),
    close: () => widget?.close(),
    destroy: () => widget?.destroy(),
  };

  try {
    Object.assign(namespace, api);
    globalScope.KernFeedback = namespace;
  } catch {
    // A frozen/immutable namespace object (e.g. a real ES module namespace):
    // fall back to a copy so the documented global access keeps working.
    globalScope.KernFeedback = { ...namespace, ...api };
  }
}

/**
 * Mount the KERN Feedback widget into a placeholder element.
 *
 * @param target CSS selector or element the mobile icon trigger renders into.
 * @param options Widget configuration (`apiKey` is required).
 * @returns A handle with `open()`, `close()` and `unmount()`.
 * @throws If `target` cannot be resolved to an element.
 */
export function mountFeedbackWidget(
  target: string | HTMLElement,
  options: MountFeedbackWidgetOptions
): FeedbackWidgetMount {
  const element = resolveMountTarget(target);

  const active = findActiveMount();
  if (active) {
    console.warn(
      active.target === element
        ? 'KERN Feedback: a widget is already mounted into this element — returning the existing mount.'
        : 'KERN Feedback: a widget is already mounted in this document — returning the existing mount. Call unmount() before mounting into a different element.'
    );
    return active;
  }

  if ((options as FeedbackWidgetConfig).triggerContainer) {
    console.warn(
      'KERN Feedback: the triggerContainer option is ignored by mountFeedbackWidget — the mount target is the trigger container.'
    );
  }

  const widget = new FeedbackWidget({
    ...options,
    triggerContainer: element,
    autoInit: false,
  });
  widget.init();

  let mounted = true;
  const mount: FeedbackWidgetMount = {
    widget,
    target: element,
    get isMounted() {
      // Also false when the widget was destroyed directly (`widget.destroy()`,
      // `window.KernFeedback.destroy()`) without going through `unmount()`.
      return mounted && widget.isInitialized;
    },
    open: () => widget.open(),
    close: () => widget.close(),
    unmount: () => {
      if (!mounted) return;
      mounted = false;
      widget.destroy();
      element.removeAttribute(MOUNT_MARKER_ATTRIBUTE);
      writeMount(element, null);
      if (activeMount === mount) {
        activeMount = null;
        exposeGlobalFeedbackApi(null);
      }
    },
  };

  element.setAttribute(MOUNT_MARKER_ATTRIBUTE, '');
  writeMount(element, mount);
  activeMount = mount;
  exposeGlobalFeedbackApi(widget);

  return mount;
}

/**
 * The widget currently mounted in this document, or `null`.
 * Useful for host code that cannot hold on to the mount handle.
 */
export function getMountedFeedbackWidget(): FeedbackWidgetMount | null {
  return findActiveMount();
}

/**
 * Find a live widget anywhere in this document by reading the instance tagged
 * onto its container element.
 *
 * Unlike {@link getMountedFeedbackWidget} this also finds widgets created
 * through the classic API (`new FeedbackWidget()` / script-tag auto-init), and
 * it works across *separate copies of the bundle* on the same page — the DOM
 * property is shared state, while module-level variables and `instanceof`
 * checks are not. Used by the auto-init duplicate guard so a second bundle
 * execution can re-publish the existing instance on `window.KernFeedback`
 * instead of building a second widget.
 */
export function findFeedbackWidgetInDocument(): FeedbackWidget | null {
  if (typeof document === 'undefined') return null;

  for (const container of Array.from(document.querySelectorAll('.kern-feedback-widget'))) {
    const value = (container as unknown as Record<string, unknown>)[WIDGET_CONTAINER_PROPERTY];
    // Duck-typed: a second bundle copy has its own FeedbackWidget class, so
    // `instanceof` would not match.
    if (
      typeof value === 'object' &&
      value !== null &&
      typeof (value as FeedbackWidget).open === 'function' &&
      typeof (value as FeedbackWidget).close === 'function' &&
      typeof (value as FeedbackWidget).destroy === 'function'
    ) {
      return value as FeedbackWidget;
    }
  }

  return null;
}
